import type { PersonCapacity } from '../../lib/peopleApi';

interface CapacityChartProps {
  data: PersonCapacity[];
}

// Distinct colors cycled per project for the stacked segments.
const PALETTE = [
  'bg-blue-500',
  'bg-emerald-500',
  'bg-violet-500',
  'bg-amber-500',
  'bg-pink-500',
  'bg-cyan-500',
];

function colorFor(projectId: string, index: Map<string, number>): string {
  if (!index.has(projectId)) index.set(projectId, index.size);
  return PALETTE[(index.get(projectId) ?? 0) % PALETTE.length];
}

export function CapacityChart({ data }: CapacityChartProps) {
  if (data.length === 0) {
    return <p className="text-gray-500">Nu există date de capacitate.</p>;
  }

  const colorIndex = new Map<string, number>();
  const weekHeaders = data[0]?.weeks ?? [];

  const shortDate = (isoDate: string) => `${isoDate.slice(8, 10)}.${isoDate.slice(5, 7)}`;

  const bar = (week: (typeof weekHeaders)[number]) => (
    <>
      {/* Stacked bar: each project segment sized by % of capacity. */}
      <div
        className={`relative flex h-6 w-full overflow-hidden rounded ${
          week.isOverallocated ? 'ring-2 ring-red-500' : 'bg-gray-100'
        }`}
        title={week.allocations
          .map((a) => `${a.projectName}: ${a.percentage}% (${a.hours}h)`)
          .join('\n')}
      >
        {week.allocations.map((a) => (
          <div
            key={a.projectId}
            className={colorFor(a.projectId, colorIndex)}
            style={{ width: `${Math.min(a.percentage, 100)}%` }}
          />
        ))}
      </div>
      <p
        className={`mt-1 text-center text-xs ${
          week.isOverallocated ? 'font-semibold text-red-600' : 'text-gray-500'
        }`}
      >
        {week.totalPercent}%
      </p>
    </>
  );

  return (
    <>
      {/* Phones: one block per person, the weeks four to a row with a short
          date over each bar; the week-per-column table only scrolled sideways. */}
      <ul className="space-y-4 sm:hidden">
        {data.map((row) => (
          <li key={row.person.id}>
            <p className="text-sm font-medium text-gray-900">{row.person.fullName}</p>
            <div className="mt-1.5 grid grid-cols-4 gap-2">
              {row.weeks.map((week) => (
                <div key={week.weekStart} className="min-w-0">
                  <p className="mb-1 text-center text-[11px] tabular-nums text-gray-400">
                    {shortDate(week.weekStart)}
                  </p>
                  {bar(week)}
                </div>
              ))}
            </div>
          </li>
        ))}
      </ul>
      <div className="hidden overflow-x-auto sm:block">
        <table className="min-w-full border-separate border-spacing-y-2">
          <thead>
            <tr className="text-left text-xs text-gray-500">
              <th className="w-40 px-2">Membru echipă</th>
              {weekHeaders.map((w) => (
                <th key={w.weekStart} className="px-2 text-center">
                  {w.weekStart}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr key={row.person.id}>
                <td className="px-2 align-top text-sm font-medium text-gray-900">
                  {row.person.fullName}
                </td>
                {row.weeks.map((week) => (
                  <td key={week.weekStart} className="px-2 align-top">
                    {bar(week)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
